import Phaser from 'phaser';
import { N1_MANIFEST_URL, N1_TEXTURE_KEY, N1_TIER } from '../config';
import { atlasDirectory } from '../assets/n1Atlas';
import { parseN1Manifest, type N1Manifest } from '../assets/n1Manifest';
import { registerN1Animations } from '../animation/registerN1Animations';
import { recordError, snapshot, publishSnapshot } from '../debug/snapshot';

export type StartScene = 'Endless' | 'Room' | 'Gallery';

/** `?scene=gallery` boots the N1 gallery, `?scene=room` the demo room; the default is the run. */
export function startSceneFromUrl(search = window.location.search): StartScene {
  const scene = new URLSearchParams(search).get('scene');
  return scene === 'gallery' ? 'Gallery' : scene === 'room' ? 'Room' : 'Endless';
}

/** Run options from the URL: `seed` pins the world, `lives` the starting lives, `autostart=1` skips the title. */
export function runOptionsFromUrl(search = window.location.search): {
  seed?: number;
  lives?: number;
  autoStart?: boolean;
  touch?: boolean;
  /** `?blend=0` disables runtime in-betweening (for A/B comparison). */
  blend?: boolean;
} {
  const params = new URLSearchParams(search);
  const num = (key: string) => {
    const v = params.get(key);
    if (v === null) return undefined;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
  };
  return {
    seed: num('seed'),
    lives: num('lives'),
    autoStart: params.get('autostart') === '1',
    touch: params.get('touch') === '1',
    blend: params.get('blend') === null ? undefined : params.get('blend') !== '0',
  };
}

/**
 * Loads and validates the N1 manifest, then the configured tier's multiatlas, registers the 13
 * animations and hands over to the room (or the gallery with `?scene=gallery`). Every failure is
 * loud: on screen, in the console and in the debug snapshot.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload(): void {
    snapshot.phase = 'loading';
    snapshot.tier = N1_TIER;
    publishSnapshot();
    this.load.json('n1-manifest', N1_MANIFEST_URL);
  }

  create(): void {
    let manifest: N1Manifest;
    try {
      manifest = parseN1Manifest(this.cache.json.get('n1-manifest'));
    } catch (error) {
      this.fail(error instanceof Error ? error.message : String(error));
      return;
    }
    const tier = manifest.tiers[N1_TIER];
    this.load.multiatlas(N1_TEXTURE_KEY, tier.multiatlas, atlasDirectory(tier.multiatlas));
    this.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      this.fail(`failed to load ${file.key} (${file.src})`);
    });
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      if (snapshot.phase === 'failed') return;
      const registered = registerN1Animations(this, manifest, N1_TEXTURE_KEY, N1_TIER);
      const missing = registered.flatMap((r) => r.missingFrames);
      if (missing.length > 0) {
        this.fail(
          `tier ${N1_TIER} is missing ${missing.length} frame(s): ${missing.slice(0, 5).join(', ')}`,
        );
        return;
      }
      snapshot.registered = registered.length;
      this.scene.start(startSceneFromUrl(), {
        manifest,
        tierLabel: N1_TIER,
        registered,
        ...runOptionsFromUrl(),
      });
    });
    this.load.start();
  }

  private fail(message: string): void {
    recordError(message);
    console.error(`[georgi] ${message}`);
    this.add
      .text(24, 24, `Georgi failed to boot:\n${message}`, {
        color: '#ff5555',
        fontSize: '18px',
        fontFamily: 'monospace',
        wordWrap: { width: this.scale.width - 48 },
      })
      .setOrigin(0, 0);
  }
}
